/*
 * Authors: mariushirjanu
 * Created: 28/03/2024
 */
import _ from 'underscore';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { CellClassParams, Column, GridOptions, RowClassParams, ValueFormatterParams } from 'ag-grid-community';
import { IPaymentMatchData, IPaymentMatchTransaction, PaymentMatcherDataService } from './payment-matcher-data.service';
import { IPaymentMatch, PaymentMatcherService } from './payment-matcher.service';
import { CSS_ACTIONS_COLUMN, IFormGridColDef } from '@app/shared/form-grid/form-grid.component';
import { CellRendererComp } from '@app/shared/ag-grid-components/ga-ag-grid.component';
import { AG_CELL_VALIDATOR, AgCellValidatorComponent } from '@app/shared/ag-grid-components/ag-cell-validator/ag-cell-validator.component';
import { TranslationService } from '@app/shared/translation/translation.service';
import { IAgButtonRendererParams } from '@app/shared/ag-grid-components/ag-button/ag-button-renderer.component';
import { FormlyInputType, FormlyType } from '@app/shared/formly/ga-formly.service';
import { AgCellValidatorService } from '@app/shared/ag-grid-components/ag-cell-validator/ag-cell-validator.service';
import { GaDeferredService, IGADeferred } from '@app/shared/services/ga-deferred.service';
import { FormattingService } from '@app/shared/services/formatting.service';
import { IRichTextGrid, IRichTextRendererParams, RichTextType } from '@app/shared/ag-grid-components/ag-rich-text/ag-rich-text-renderer.component';
import { urlUtil } from '@app/shared/util';
import { URL_INVOICE_EDIT } from '@app/shared/browser/url-collections';
import { GaAgGridService } from '@app/shared/ag-grid-components/ga-ag-grid.service';
import { GRID_PAYMENT_LIST_MATCHES } from '@app/shared/app-grids';

const MATCHED_AMOUNT_COLUMN:string = 'matchedAmount';

@Component({
  selector: 'payment-list-matches',
  template: `
    <h4 class="form-title">Payment Matches</h4>

    <div class="alert alert-danger" *ngIf="!loading && errors?.length">
      <div><i class="fa fa-exclamation-triangle"></i> Please fix the error messages before continuing: {{ errors[0] }}</div>
    </div>
    
    <form-grid
      [gridId]="gridId"
      [loading]="loading"
      [gridOptions]="gridOptions"
      [rowData]="rowData"
      [allowEdit]="true"
      [height]="formGridHeight"
      [stopEditing]="stopEditing"
      (onChange)="onModelChange($event)">
    </form-grid>

    <payment-list-matches-amounts [model]="model" [loading]="loading" [rowData]="rowData"></payment-list-matches-amounts>
  `,
  styleUrls: ['./payment-matcher-dialog.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PaymentListMatchesComponent implements OnInit, OnChanges {
  @Input() public model:IPaymentMatchTransaction;
  @Input() public matchToAdd:IPaymentMatch;
  @Output() public onRemoveFromMatches:EventEmitter<IPaymentMatch> = new EventEmitter<IPaymentMatch>();
  @Output() public onChange:EventEmitter<IPaymentMatch[]> = new EventEmitter<IPaymentMatch[]>();

  public gridId:string = GRID_PAYMENT_LIST_MATCHES;
  public stopEditing:boolean;
  public loading:boolean;
  public gridOptions:GridOptions;
  public formGridHeight:number;
  public errors:string[];
  private _rowData:IPaymentMatch[];

  constructor(
    private agCellValidatorService:AgCellValidatorService,
    private deferredService:GaDeferredService,
    private formattingService:FormattingService,
    private paymentMatcherDataService:PaymentMatcherDataService,
    private paymentMatcherService:PaymentMatcherService,
    private translationService:TranslationService,
    private gaAgGridService:GaAgGridService,
    private changeDetectorRef:ChangeDetectorRef
  ) {
    /***/
  }

  public ngOnInit():void {
    this.formGridHeight = this.paymentMatcherService.calcFormGridHeight;
    this.installGrid();
    this.loadData();
  }

  public ngOnChanges(changes:SimpleChanges):void {
    // receive new invoices removed from invoices list to add to payment matches list
    if (changes.matchToAdd?.currentValue) {
      this.matchToAdd = changes.matchToAdd.currentValue;
      this.addMatch();
    }
  }

  private get colDefs():IFormGridColDef[] {
    const statusNewOrProposed:boolean = this.paymentMatcherService.isStatusNewOrProposed(this.model.processingStatus);
    const colDefs:IFormGridColDef[] = [
      {
        pinned: 'left',
        minWidth: 86,
        width: 86,
        cellClass: CSS_ACTIONS_COLUMN,
        tooltipValueGetter: () => statusNewOrProposed ? 'Reject match' : '',
        cellRenderer: CellRendererComp.BUTTON_RENDERER,
        cellRendererParams: {
          label: this.translationService.instant('GA.BUTTONS.REJECT'),
          icon: 'fa-arrow-down',
          disabled: !statusNewOrProposed,
          onClick: (params:IAgButtonRendererParams) => {
            this.rejectMatch(params.data?.id);
          },
          className: 'btn-sm btn-danger'
        }
      },
      {
        field: 'id',
        headerName: 'Invoice ID',
        cellRenderer: CellRendererComp.RICH_TEXT_RENDERER,
        cellRendererParams: <IRichTextRendererParams> {
          type: RichTextType.LINK,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => [{
            label: params.value,
            href: urlUtil.replaceParams(URL_INVOICE_EDIT, { invoiceId: params.value })
          }]
        }
      },
      {
        field: 'invoiceNumber',
        headerName: 'Invoice number'
      },
      {
        field: 'orderId',
        headerName: 'Request ID'
      },
      {
        field: 'poNumber',
        headerName: 'PO number'
      },
      {
        field: 'currency',
        headerName: 'Currency'
      },
      {
        field: 'amount',
        headerName: 'Amount',
        type: 'rightAligned',
        valueFormatter: this.formatNumber
      },
      {
        field: 'remainingAmount',
        headerName: 'Remaining',
        type: 'rightAligned',
        valueFormatter: this.formatNumber
      },
      {
        field: MATCHED_AMOUNT_COLUMN,
        headerName: 'Matched amount',
        type: 'rightAligned',
        valueFormatter: this.formatNumber,
        editable: true,
        cellEditorParams: {
          type: FormlyType.INPUT,
          inputType: FormlyInputType.NUMBER,
          required: true,
          min: 0.01,
          valueFormatter: (value:number) => {
            return Math.round(value * 100) / 100; // round the amount value to 2 decimals (prevents read-only having different value than the input)
          }
        }
      },
      {
        field: 'invoicedTo',
        headerName: 'Invoice to name'
      }
    ];

    return _.map(colDefs, (colDef:IFormGridColDef) => {
      colDef.cellRenderer = colDef.cellRenderer || AG_CELL_VALIDATOR; // apply 'agCellValidator' to columns in order to display validation errors
      colDef.tooltipField = ''; // hide default ag-grid tooltip (instead is being used the popover defined in AG_CELL_VALIDATOR)

      colDef.minWidth = colDef.minWidth || 100;
      colDef.resizable = true;
      colDef.editable = colDef.editable || false;
      colDef.sortable = true;

      if(colDef.editable) {
        // apply css classes to the cells
        colDef.cellClassRules = {
          // apply 'cell-error' css class when there is an error message -> this adds to the cell: red border, red color, ...
          'cell-error': (params:CellClassParams):boolean => !!this.agCellValidatorService.validateCell(params).length
        };
      }

      return colDef;
    });
  }

  private installGrid():void {
    this.gridOptions = {
      columnDefs: this.colDefs,
      components: {
        [AG_CELL_VALIDATOR]: AgCellValidatorComponent // display assets validation errors
      },
      stopEditingWhenCellsLoseFocus: false, // when set to false -> stop editing is done manually because the input field needs time to focus out before stopping editing
      onRowDataUpdated: () => {
        //auto size all column except row-actions
        const columns:Column[] = _.reject(this.gaAgGridService.getColumns(this.gridId), (column:Column) => column.getColDef().cellClass === CSS_ACTIONS_COLUMN);
        return this.gaAgGridService.autoSizeColumns(this.gridId, columns);
      },
      rowClassRules: {
        // highlight new added rows
        'highlight-row': (params:RowClassParams) => {
          return params.data?.highlight;
        }
      }
    };
  }

  private loadData():void {
    this.loading = true;
    this.paymentMatcherDataService.getMatches(this.model?.transactionId)
      .then((paymentMatchesData:IPaymentMatchData[]) => {
        this.rowData = this.paymentMatcherService.getPaymentMatch(paymentMatchesData);
      })
      .catch(() => {
        this.rowData = []; // when promise fails -> stop spinner
      })
      .finally(() => {
        this.loading = false;
        this.changeDetectorRef.detectChanges();
      });
  }

  public onModelChange(model:IPaymentMatch[]):void {
    this.rowData = model;
  }

  private rejectMatch(transactionId:string):void {
    const rejectedMatch:IPaymentMatch = _.find(this.rowData, (rowData:IPaymentMatch) => rowData.id === transactionId);
    this.onRemoveFromMatches.emit(rejectedMatch);
    this.rowData = _.reject(this.rowData, (rowData:IPaymentMatch) => rowData.id === transactionId);
  }

  private addMatch():void {
    if(this.matchToAdd?.id) {
      this.matchToAdd.highlight = true;
      this.matchToAdd.matchedAmount = this.matchToAdd.amount; // pre-fill with the invoice amount
      this.rowData = [ this.matchToAdd ].concat(this.rowData);

      // wait to set the new rowData
      setTimeout(() => {
        // start editing the matched amount cell
        this.gaAgGridService.startEditing(this.gridId, {
          rowIndex: 0,
          colKey: MATCHED_AMOUNT_COLUMN // use the last column
        });
      }, 50);
    }
  }

  public validate():Promise<string[] | void> {
    const deferred:IGADeferred<string[]> = this.deferredService.defer();

    // before performing validation -> exit ag-grid editing mode to apply validation to the cell
    this.stopEditing = !this.stopEditing;

    // wait little bit to allow ag-grid to conclude stop editing before initiating validation
    setTimeout(() => {
      this.agCellValidatorService.validateEditableCells(this.colDefs, this.gridOptions, this.gridId)
        .then(deferred.resolve)
        .catch((errors:string[]) => {
          this.errors = errors;
          deferred.reject(errors);
        });
    });

    return deferred.promise;
  }

  public get rowData():IPaymentMatch[] {
    return this._rowData;
  }

  public set rowData(paymentMatches:IPaymentMatch[]){
    this._rowData = paymentMatches;
    this.onChange.emit(this.rowData);
    this.changeDetectorRef.detectChanges();
  }

  private formatNumber = (params:ValueFormatterParams):string => {
    return this.formattingService.formatNumber(params.value);
  };
}